import { Radio as BaseRadio } from '@base-ui/react/radio';
import { RadioGroup as BaseRadioGroup } from '@base-ui/react/radio-group';
import { RiUpload2Line } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { Icon, type IconName } from './icon';
import { DashedOutline } from './lib/dashed-outline';

/** Section icons of the "Yangi bo'lim" modal (02-constructor §3.6, 317:7983). */
export const SECTION_ICON_NAMES: readonly IconName[] = [
  'drop-line',
  'dashboard-line',
  'thermometer-line',
  'map-pin-line',
  'time-line',
  'calendar-line',
  'image-line',
  'file-gif-line',
  'play-line',
  'heading',
];

export interface IconPickerProps {
  value?: string | null;
  onValueChange: (name: string) => void;
  /** Registry names to offer. Default: section icons. */
  icons?: readonly string[];
  /** Adds the dashed upload cell first ("IKONKA" upload tile). */
  onUpload?: () => void;
  uploadLabel?: string;
  /**
   * `md` — modal cells 44×44 r10 White/5, selected accent, glyph 18, gap 8 (317:7983).
   * `sm` — popover cells 36×36 r8 White/10, selected accent/20 + 1px accent, glyph 16 (02-constructor §2.5).
   */
  size?: 'md' | 'sm';
  'aria-label'?: string;
  className?: string;
}

/** Grid of icon tiles (radio-group semantics). */
export function IconPicker({
  value,
  onValueChange,
  icons = SECTION_ICON_NAMES,
  onUpload,
  uploadLabel = 'Ikonka yuklash',
  size = 'md',
  'aria-label': ariaLabel = 'Ikonka',
  className,
}: IconPickerProps) {
  const md = size === 'md';
  const cell = cn(
    'relative flex shrink-0 cursor-pointer items-center justify-center text-icon outline-none transition-colors duration-150',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-2',
    md ? 'size-44 rounded-10 bg-subtle hover:bg-field' : 'size-36 rounded-8 bg-field hover:bg-line-strong',
  );
  return (
    <div className={cn('flex flex-wrap gap-8', className)}>
      {onUpload && (
        <button type="button" aria-label={uploadLabel} onClick={onUpload} className={cell}>
          <DashedOutline radius={md ? 10 : 8} className="text-line-strong" />
          <RiUpload2Line aria-hidden className={md ? 'size-18' : 'size-16'} />
        </button>
      )}
      <BaseRadioGroup aria-label={ariaLabel} value={value ?? null} onValueChange={(next) => onValueChange(String(next))} className="contents">
        {icons.map((name) => (
          <BaseRadio.Root
            key={name}
            value={name}
            aria-label={name}
            className={cn(
              cell,
              md
                ? 'data-checked:bg-accent data-checked:text-white'
                : 'data-checked:border data-checked:border-accent data-checked:bg-accent-soft data-checked:text-fg',
            )}
          >
            <Icon name={name} className={md ? 'size-18' : 'size-16'} />
          </BaseRadio.Root>
        ))}
      </BaseRadioGroup>
    </div>
  );
}
